import { useCOBClientBreakdown, useCOBStats } from "creso-core/hooks";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { COBClientDetail } from "creso-core/types";
import {
  Button,
  Checkbox,
  DataTable,
  DataTablePagination,
  Popover,
  PopoverContent,
  PopoverTrigger,
  V2Loader,
  ScrollArea,
} from "web-design-system";
import { formatAsCrore, getInitials } from "creso-core/utils";
import { Avatar } from "web-core";
import { FilterIcon } from "lucide-react";
import { Link, useRouter } from "@tanstack/react-router";
import { Page } from "@core/components/layout/page";
import { COBViewDetailsDialog } from "../components/cob-view-details-dialog";
import { CobStatusProgress } from "../components/cob-status-progress";
import { CobProvider } from "../providers/cob-provider";
import { CobClientActionCell } from "../components/cob-client-action-cell";

function StatCard({
  label,
  aum,
  clientCount,
}: {
  label: string;
  aum: number;
  clientCount: number;
}) {
  return (
    <div className="flex flex-col gap-2 border border-border rounded-lg px-4 py-3 flex-1 bg-card">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex gap-2 items-center">
        <span className="text-base font-semibold">{formatAsCrore(aum)}</span>
        <span className="text-sm text-muted-foreground">
          ({clientCount} {clientCount === 1 ? "client" : "clients"})
        </span>
      </div>
    </div>
  );
}

export default function CobDashboard() {
  return (
    <CobProvider>
      <ChangeOfBroker />
    </CobProvider>
  );
}

const COB_STATUS_OPTIONS = [
  { label: "Downloaded", value: "DOWNLOADED" },
  { label: "In Transit", value: "IN_TRANSIT" },
  { label: "Under Processing", value: "UNDER_PROCESSING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Rejected", value: "REJECTED" },
];

const columnHelper = createColumnHelper<COBClientDetail>();

function ChangeOfBroker() {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const { data, isLoading } = useCOBStats();

  const { data: clients } = useCOBClientBreakdown({
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
  });

  function toggleStatus(value: string) {
    setSelectedStatuses((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor("client_name", {
        header: "Client Name",
        minSize: 200,
        meta: { className: "px-4 py-3" },
        cell: ({ getValue, row }) => {
          const name = getValue();
          return (
            <div className="flex items-center gap-2">
              <Avatar fallback={getInitials(name ?? "")} size="sm" />
              <Link
                to="/clients/$id/portfolio"
                params={{ id: row.original.client_id?.toString() }}
                className="text-sm"
              >
                {name}
              </Link>
            </div>
          );
        },
      }),
      columnHelper.accessor("external_aum", {
        header: () => <div className="text-left">External AUM</div>,
        meta: { className: "px-4 py-3 text-left" },
        cell: ({ getValue }) => (
          <span className="text-sm">
            {new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
              maximumFractionDigits: 0,
            }).format(getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("folio_count", {
        header: () => <div className="text-left">No. of Folios</div>,
        meta: { className: "px-4 py-3 text-left" },
        cell: ({ getValue }) => <span className="text-sm">{getValue()}</span>,
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        meta: { className: "px-4 py-3 text-left" },
        cell: ({ row }) => <CobStatusProgress client={row.original} />,
      }),
      columnHelper.display({
        id: "actions",
        meta: { className: "px-4 py-3 text-right" },
        cell: ({ row }) => <CobClientActionCell client={row.original} />,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: clients?.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: clients?.count ?? 0,
    state: { pagination },
  });

  const router = useRouter();

  return (
    <>
      <Page.Header
        title="Change of Broker"
        actions={
          <Button
            onClick={() =>
              router.navigate({
                to: ".",
                search: (prev) => ({
                  ...prev,
                  modal: "change-of-broker",
                }),
              })
            }
          >
            Add New
          </Button>
        }
      />

      <Page.Body className="flex-col gap-4">
        <div className="flex flex-row gap-3">
          <StatCard
            label="In pipeline"
            aum={data?.overall.in_pipeline.aum ?? 0}
            clientCount={data?.overall.in_pipeline.client_count ?? 0}
          />
          <StatCard
            label="AUM transferred"
            aum={data?.overall.aum_transferred.aum ?? 0}
            clientCount={data?.overall.aum_transferred.client_count ?? 0}
          />
          <StatCard
            label="Transferable external AUM"
            aum={data?.overall.transferable_external_aum.aum ?? 0}
            clientCount={
              data?.overall.transferable_external_aum.client_count ?? 0
            }
          />
        </div>

        <div className="flex flex-col flex-1 overflow-hidden gap-3">
          <div className="flex flex-row justify-between items-center">
            <span className="text-lg font-semibold">Client Pipeline</span>
            <div className="flex flex-row gap-2 items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline">
                    <FilterIcon />
                    Filters
                    {selectedStatuses.length > 0 && (
                      <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                        {selectedStatuses.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-52 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold">Filter by</span>
                    {selectedStatuses.length > 0 && (
                      <button
                        className="text-xs underline text-muted-foreground hover:text-foreground"
                        onClick={() => setSelectedStatuses([])}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col gap-3">
                    {COB_STATUS_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedStatuses.includes(option.value)}
                          onCheckedChange={() => toggleStatus(option.value)}
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <V2Loader
            isLoading={isLoading}
            className="flex flex-col flex-1 w-full overflow-hidden border border-border rounded-lg"
          >
            <ScrollArea className="flex-1 flex flex-col">
              <DataTable
                pagination={false}
                table={table}
                className="border-none"
              />
            </ScrollArea>
            <DataTablePagination
              table={table}
              className="sticky bottom-0 z-20"
            />
          </V2Loader>
        </div>
        <COBViewDetailsDialog />
      </Page.Body>
    </>
  );
}
